import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { Dimensions, Pressable, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Carousel from 'react-native-reanimated-carousel';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { usePostHog } from 'posthog-js/react';
import { COLORS, REDESIGN_COLORS } from '../../styles/colors';
import FallbackImage from '../common/FallbackImage';
import { useResponsive } from '../../hooks/useResponsive';
import { useTier } from '../../context/TierContext';
import OverallMatchBadge from '../feed/OverallMatchBadge';
import ListingBadgeStack from '../feed/ListingBadgeStack';
import { buildListingBadges } from '../../utils/listingBadges';

// ============================================================
// Constants
// ============================================================

const BREAKPOINT_WIDTH = 768;

/**
 * Format price in GBP with thousands separator.
 * @param {number|null} price
 * @returns {string}
 */
const formatPrice = (price) => {
  if (price == null || isNaN(price)) return '--';
  return `\u00A3${Number(price).toLocaleString('en-GB')}`;
};

/**
 * Format mileage with comma separator and "mi" suffix.
 * @param {number|null} miles
 * @returns {string}
 */
const formatMileage = (miles) => {
  if (miles == null || isNaN(miles)) return '--';
  return `${Number(miles).toLocaleString('en-GB')} mi`;
};

// ============================================================
// Sub-components
// ============================================================

/** Heart icon (save/unsave toggle). */
const SaveButton = memo(({ isSaved, onPress, isSaving }) => (
  <Pressable
    style={styles.actionButton}
    onPress={onPress}
    disabled={isSaving}
    accessibilityRole="button"
    accessibilityLabel={isSaved ? 'Unsave listing' : 'Save listing'}
  >
    <Icon
      name={isSaved ? 'heart' : 'heart-outline'}
      size={22}
      color={isSaved ? '#EF4444' : '#FFFFFF'}
    />
  </Pressable>
));
SaveButton.displayName = 'SaveButton';

/** X button (dismiss). */
const DismissButton = memo(({ onPress, isDismissing }) => (
  <Pressable
    style={styles.actionButton}
    onPress={onPress}
    disabled={isDismissing}
    accessibilityRole="button"
    accessibilityLabel="Dismiss listing"
  >
    <Icon name="close" size={20} color="#FFFFFF" />
  </Pressable>
));
DismissButton.displayName = 'DismissButton';

/** Model match percentage from user_model_fit_scores. */
const ModelMatchBadge = memo(({ fitScore }) => {
  if (fitScore == null) return null;
  return (
    <View style={styles.modelMatchBadge}>
      <Icon name="check-decagram" size={14} color={COLORS.primary} />
      <Text style={styles.modelMatchText}>{Math.round(fitScore)}% Match</Text>
    </View>
  );
});
ModelMatchBadge.displayName = 'ModelMatchBadge';

/** "FCA Dealer" badge. */
const FCADealerBadge = () => (
  <View style={styles.fcaBadge}>
    <Icon name="shield-check" size={12} color="#16A34A" />
    <Text style={styles.fcaBadgeText}>FCA Dealer</Text>
  </View>
);

/** Price change overlay showing direction arrow, amount, and previous price. */
const PriceChangeOverlay = memo(({ direction, amount, previousPrice }) => {
  if (!direction || !amount) return null;
  const isDown = direction === 'down';
  const color = isDown ? '#16A34A' : '#EF4444';
  const arrow = isDown ? 'arrow-down' : 'arrow-up';
  return (
    <View style={styles.priceChangeContainer}>
      <View style={[styles.priceChangeChip, { backgroundColor: isDown ? 'rgba(22,163,74,0.10)' : 'rgba(239,68,68,0.10)' }]}>
        <Icon name={arrow} size={14} color={color} />
        <Text style={[styles.priceChangeAmount, { color }]}>
          {formatPrice(Math.abs(amount))}
        </Text>
      </View>
      <Text style={styles.previousPrice}>{formatPrice(previousPrice)}</Text>
    </View>
  );
});
PriceChangeOverlay.displayName = 'PriceChangeOverlay';

/** Sold/removed overlay with AI-generated vehicle_model_images fallback. */
const NoLongerAvailableOverlay = () => (
  <View style={styles.unavailableOverlay}>
    <View style={styles.unavailableChip}>
      <Icon name="car-off" size={16} color={COLORS.white} />
      <Text style={styles.unavailableText}>No Longer Available</Text>
    </View>
  </View>
);

// ============================================================
// Main component
// ============================================================

/**
 * ListingCard - Individual listing card for the Daily Listings Feed.
 *
 * Displays a vehicle listing with score, badges, price, interactions,
 * and responsive layout (mobile < 768px, desktop >= 768px).
 *
 * @param {Object}   props
 * @param {Object}   props.listing        - Feed entry from user_listing_feeds (incl. listing_snapshot, score, etc.)
 * @param {number}   [props.fitScore]     - Model-level fit score from user_model_fit_scores (0-100)
 * @param {string}   [props.vehicleModelImageUrl] - AI-generated vehicle model image (fallback for sold)
 * @param {boolean}  [props.isSaving]     - Whether a save request is in-flight
 * @param {boolean}  [props.isDismissing] - Whether a dismiss request is in-flight
 * @param {boolean}  [props.isTopMatch]   - Cohort flag for the TOP MATCH overlay badge
 * @param {boolean}  [props.isBestDeal]   - Cohort flag for the BEST DEAL overlay badge
 * @param {Function} props.onPress        - Card tap handler
 * @param {Function} props.onSave         - Save/unsave handler (receives listing.id)
 * @param {Function} props.onDismiss      - Dismiss handler (receives listing.id)
 * @param {boolean}  [props.isLocked]     - Trial-tier lock for positions >= 2 per model; taps open upgrade modal
 */
const ListingCard = ({
  listing,
  fitScore,
  vehicleModelImageUrl,
  isSaving = false,
  isDismissing = false,
  isTopMatch = false,
  isBestDeal = false,
  onPress,
  onSave,
  onDismiss,
  isLocked = false,
}) => {
  const { isMobile, screenWidth } = useResponsive();
  const { triggerUpgradeModal, trialUnlockEligible } = useTier();
  const posthog = usePostHog();

  // ---- Derived data from listing_snapshot + feed entry ----
  const snapshot = listing?.listing_snapshot || {};
  const {
    make,
    model,
    trim: variant,
    year,
    price,
    miles,
    transmission,
    city,
    distance_miles: distanceMiles,
    photo_url: snapshotPhoto,
    fca_status: fcaStatus,
  } = snapshot;

  const score = listing?.score ?? 0;
  const isSaved = !!listing?.saved_at;
  const isUnavailable = listing?.status === 'sold' || listing?.status === 'removed';

  // Model AI image used as the display-time fallback when a dealer photo fails to load
  // (generalises the existing sold-only vehicleModelImageUrl to every state). Prefer the
  // explicit prop, else the value attached to the listing payload by get-model-listings.
  const modelFallbackUrl = vehicleModelImageUrl || listing?.modelImageUrl || null;

  const badges = useMemo(
    () => buildListingBadges(listing, { isTopMatch, isBestDeal }),
    [listing, isTopMatch, isBestDeal]
  );

  // Resolve photo list for carousel, falling back to single photo_url.
  const photoLinks = useMemo(() => {
    if (isUnavailable && vehicleModelImageUrl) return [vehicleModelImageUrl];
    const links = listing?.listing_snapshot?.photo_links;
    if (Array.isArray(links) && links.length > 0) return links;
    if (listing?.listing_snapshot?.photo_url) return [listing.listing_snapshot.photo_url];
    return [];
  }, [isUnavailable, vehicleModelImageUrl, listing?.listing_snapshot?.photo_links, listing?.listing_snapshot?.photo_url]);

  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const carouselRef = useRef(null);

  // Per-listing below-market delta (£) parsed from the scoring engine's
  // price_vs_market explanation (e.g. "£1,800 below market average"). Mirrors
  // the regex in FeedListingCard's matchSnippet. Used to personalize the unlock
  // surface ("This one is £1,800 under market."). Null when this listing isn't
  // below market or carries no breakdown.
  const belowMarketDelta = useMemo(() => {
    const breakdown = listing?.score_breakdown;
    if (!Array.isArray(breakdown)) return null;
    const entry = breakdown.find((s) => s?.signal === 'price_vs_market');
    const m = (entry?.explanation || '').match(/£([\d,]+)\s+below/i);
    if (!m) return null;
    const amount = parseInt(m[1].replace(/,/g, ''), 10);
    return Number.isFinite(amount) && amount >= 200 ? amount : null;
  }, [listing?.score_breakdown]);

  // ---- Handlers (stable refs via useCallback) ----
  const handleUnlockTap = useCallback((e) => {
    if (e?.stopPropagation) e.stopPropagation();
    posthog?.capture?.('locked_listing_unlock_tapped', { feed_entry_id: listing?.id, surface: 'car_detail_listings' });
    triggerUpgradeModal?.({ source: 'cap_feed_listings', belowMarketDelta });
  }, [posthog, listing?.id, triggerUpgradeModal, belowMarketDelta]);

  const handlePress = useCallback((e) => {
    if (isLocked) {
      handleUnlockTap(e);
      return;
    }
    // Don't navigate if click originated from an interactive child (save/dismiss/carousel arrows)
    if (e?.target?.closest?.('button, [role="button"]') && e.target.closest('button, [role="button"]') !== e.currentTarget) return;
    if (onPress) onPress(listing);
  }, [isLocked, handleUnlockTap, onPress, listing]);

  const handleSave = useCallback(
    (e) => {
      // Stop event from bubbling to onPress
      if (e?.stopPropagation) e.stopPropagation();
      if (onSave) onSave(listing?.id);
    },
    [onSave, listing?.id]
  );

  const handleDismiss = useCallback(
    (e) => {
      if (e?.stopPropagation) e.stopPropagation();
      if (onDismiss) onDismiss(listing?.id);
    },
    [onDismiss, listing?.id]
  );

  // ---- Responsive dynamic styles ----
  const cardWidth = isMobile ? screenWidth - 32 : 360;

  const dynamicStyles = useMemo(
    () => ({
      card: {
        width: cardWidth,
        maxWidth: 400,
      },
      image: {
        width: '100%',
        height: isMobile ? 180 : 200,
      },
    }),
    [cardWidth, isMobile]
  );

  // ---- Render ----
  if (!listing) return null;

  return (
    <View
      style={[styles.card, dynamicStyles.card]}
      accessibilityRole="link"
      accessibilityLabel={isLocked ? `Locked ${year} ${make} ${model} listing, unlock with Pro` : `${year} ${make} ${model} listing, score ${Math.round(score)} out of 100`}
      onClick={handlePress}
    >
      {/* Image section */}
      <View
        style={[styles.imageContainer, dynamicStyles.image, isLocked && styles.imageContainerLocked]}
        onLayout={(e) => {
          const { width, height } = e.nativeEvent.layout;
          setContainerSize({ width, height });
        }}
      >
        {photoLinks.length > 0 && containerSize.width > 0 ? (
          <>
            <Carousel
              ref={carouselRef}
              loop={photoLinks.length > 1}
              width={containerSize.width}
              height={containerSize.height || (isMobile ? 180 : 200)}
              autoPlay={false}
              data={photoLinks}
              scrollAnimationDuration={300}
              onSnapToItem={setActivePhotoIndex}
              renderItem={({ item }) => (
                <FallbackImage
                  primitive="rn"
                  dealerPhotoUrl={item}
                  modelImageUrl={modelFallbackUrl}
                  style={styles.carouselImage}
                  resizeMode="cover"
                />
              )}
              onConfigurePanGesture={(gesture) => {
                if (gesture.config) gesture.config.touchAction = 'pan-y';
                gesture.activeOffsetX([-10, 10]);
                gesture.failOffsetY([-5, 5]);
              }}
            />
            {photoLinks.length > 1 && (
              <>
                <TouchableOpacity
                  style={[styles.carouselArrow, styles.carouselArrowLeft]}
                  onPress={() => carouselRef.current?.prev()}
                >
                  <Icon name="chevron-left" size={22} color="#000" />
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.carouselArrow, styles.carouselArrowRight]}
                  onPress={() => carouselRef.current?.next()}
                >
                  <Icon name="chevron-right" size={22} color="#000" />
                </TouchableOpacity>
                <View style={styles.photoCountBadge}>
                  <Icon name="camera" size={12} color={COLORS.white} />
                  <Text style={styles.photoCountText}>
                    {activePhotoIndex + 1}/{photoLinks.length}
                  </Text>
                </View>
              </>
            )}
          </>
        ) : (photoLinks.length > 0 || modelFallbackUrl) ? (
          <FallbackImage
            primitive="rn"
            dealerPhotoUrl={photoLinks[0] || null}
            modelImageUrl={modelFallbackUrl}
            style={styles.carouselImage}
            resizeMode="cover"
          />
        ) : (
          <View style={[styles.imagePlaceholder, dynamicStyles.image]}>
            <Icon name="car" size={48} color={COLORS.mediumGray} />
            <Text style={styles.placeholderText}>No Image</Text>
          </View>
        )}

        {/* Overlay badges (top-left stack) — shared with feed + compare */}
        {!isUnavailable && !isLocked && <ListingBadgeStack badges={badges} />}

        {/* Save / Dismiss buttons (top-right) */}
        {!isUnavailable && !isLocked && (
          <View style={styles.cardActions}>
            <SaveButton isSaved={isSaved} onPress={handleSave} isSaving={isSaving} />
            <DismissButton onPress={handleDismiss} isDismissing={isDismissing} />
          </View>
        )}

        {/* No Longer Available full overlay */}
        {isUnavailable && <NoLongerAvailableOverlay />}
      </View>

      {/* Trial-tier lock overlay (positions >= 2 per model). Rendered as a
          sibling of imageContainer so the CSS blur filter on the image does
          not blur the CTA button. */}
      {isLocked && (
        <Pressable
          style={styles.lockOverlay}
          onPress={handleUnlockTap}
          accessibilityRole="button"
          accessibilityLabel="Unlock with Pro"
        >
          <View style={styles.lockButton}>
            <Icon name="lock-outline" size={14} color="#FFFFFF" />
            <Text style={styles.lockButtonText}>{trialUnlockEligible ? 'Unlock' : 'Unlock with Pro'}</Text>
          </View>
        </Pressable>
      )}

      {/* Content section */}
      <View style={[styles.content, isLocked && styles.contentLocked]}>
        {/* Scores row */}
        <View style={styles.scoresRow}>
          <ModelMatchBadge fitScore={fitScore} />
          <OverallMatchBadge listing={listing} />
        </View>

        {/* Title: Make Model Variant Year */}
        <Text style={styles.title} numberOfLines={1} ellipsizeMode="tail">
          {year} {make} {model}
          {variant ? ` ${variant}` : ''}
        </Text>

        {/* Specs row: mileage + transmission */}
        <View style={styles.specsRow}>
          <View style={styles.specItem}>
            <Icon name="speedometer" size={14} color="rgba(255, 255, 255, 0.5)" />
            <Text style={styles.specText}>{formatMileage(miles)}</Text>
          </View>
          {transmission && (
            <View style={styles.specItem}>
              <Icon name="car-shift-pattern" size={14} color="rgba(255, 255, 255, 0.5)" />
              <Text style={styles.specText}>{transmission}</Text>
            </View>
          )}
        </View>

        {/* Location row: city + distance */}
        <View style={styles.locationRow}>
          <Icon name="map-marker-outline" size={14} color="rgba(255, 255, 255, 0.5)" />
          <Text style={styles.locationText}>
            {city || 'Unknown'}
            {distanceMiles != null ? ` \u00B7 ${Math.round(distanceMiles)} mi away` : ''}
          </Text>
        </View>

        {/* Price section */}
        <View style={styles.priceSection}>
          <Text style={styles.price}>{formatPrice(listing?.current_price ?? price)}</Text>
          <PriceChangeOverlay
            direction={listing?.price_change_direction}
            amount={listing?.price_change_amount}
            previousPrice={listing?.previous_price}
          />
        </View>

        {fcaStatus === 'Authorised' && (
          <View style={styles.footer}>
            <FCADealerBadge />
          </View>
        )}
      </View>
    </View>
  );
};

// ============================================================
// Styles
// ============================================================

const styles = StyleSheet.create({
  // Card container
  card: {
    backgroundColor: '#1D242F',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    overflow: 'hidden',
    boxShadow: '0px 2px 16px rgba(0, 0, 0, 0.3)',
    marginBottom: 16,
    cursor: 'pointer',
  },

  // Image
  imageContainer: {
    position: 'relative',
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: 200,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
  imagePlaceholder: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
  placeholderText: {
    marginTop: 4,
    fontFamily: 'Satoshi-Regular',
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.4)',
  },
  carouselImage: {
    width: '100%',
    height: '100%',
  },
  carouselArrow: {
    position: 'absolute',
    top: '50%',
    transform: [{ translateY: -16 }],
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.75)',
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.15)',
  },
  carouselArrowLeft: {
    left: 6,
  },
  carouselArrowRight: {
    right: 6,
  },
  photoCountBadge: {
    position: 'absolute',
    bottom: 8,
    right: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  photoCountText: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 11,
    color: COLORS.white,
  },

  // Action buttons (top-right)
  cardActions: {
    position: 'absolute',
    top: 8,
    right: 8,
    flexDirection: 'row',
    gap: 4,
  },
  actionButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    justifyContent: 'center',
    alignItems: 'center',
  },

  // Trial-tier lock overlay (matches FeedListingCard on /home)
  imageContainerLocked: {
    filter: 'grayscale(1) blur(10px)',
  },
  contentLocked: {
    filter: 'blur(6px)',
    opacity: 0.5,
    pointerEvents: 'none',
  },
  lockOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 5,
    cursor: 'pointer',
  },
  lockButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: REDESIGN_COLORS.ctaBlue,
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  lockButtonText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 13,
    color: '#FFFFFF',
  },

  // Unavailable overlay
  unavailableOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  unavailableChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
  },
  unavailableText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 13,
    color: COLORS.white,
  },

  // Content
  content: {
    padding: 14,
  },

  // Scores row
  scoresRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
    flexWrap: 'wrap',
  },
  modelMatchBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: 'rgba(5, 86, 247, 0.2)',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  modelMatchText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 12,
    color: '#5B8DEF',
  },

  // Title
  title: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 16,
    color: '#FFFFFF',
    lineHeight: 22,
    marginBottom: 6,
  },

  // Specs row
  specsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 4,
  },
  specItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  specText: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 13,
    color: 'rgba(255, 255, 255, 0.6)',
  },

  // Location
  locationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginBottom: 10,
  },
  locationText: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 13,
    color: 'rgba(255, 255, 255, 0.6)',
  },

  // Price section
  priceSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  price: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 20,
    color: '#FFFFFF',
    lineHeight: 28,
  },
  priceChangeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  priceChangeChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  priceChangeAmount: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 12,
  },
  previousPrice: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.4)',
    textDecorationLine: 'line-through',
  },

  // Footer
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTopWidth: 1,
    borderTopColor: 'rgba(255, 255, 255, 0.1)',
    paddingTop: 12,
  },
  fcaBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: 'rgba(22, 163, 74, 0.08)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  fcaBadgeText: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 11,
    color: '#16A34A',
  },
});

export default memo(ListingCard);
